import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, CheckCircle2, Loader2, Check } from 'lucide-react';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

// Reusable card-option (defined at module scope so visual-edits babel plugin can trace props cleanly)
const OptionCard = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`group relative w-full text-left p-5 rounded-2xl border transition-all duration-500 font-body text-[0.95rem] flex items-center justify-between ${
      active
        ? 'border-accent bg-accent/10 text-text-primary'
        : 'border-border bg-background hover:border-accent/60 text-text-secondary hover:text-text-primary'
    }`}
  >
    <span>{children}</span>
    <span className={`flex items-center justify-center w-6 h-6 rounded-full border transition-all duration-500 ${
      active ? 'bg-accent border-accent text-background' : 'border-border text-transparent group-hover:border-accent/60'
    }`}>
      <Check size={12} strokeWidth={2} />
    </span>
  </button>
);

const WorkWithMeModal = ({ isOpen, onClose }) => {
  const { t, language } = useLanguage();
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [formData, setFormData] = useState({
    brandName: '',
    brandLink: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    platforms: [],
    videoCount: '',
    videoLength: '',
    extraAds: '',
    postingDate: '',
    hasBrief: '',
  });

  const totalSteps = 8;

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePlatformToggle = (platform) => {
    setFormData((prev) => {
      const cur = prev.platforms;
      return cur.includes(platform)
        ? { ...prev, platforms: cur.filter((p) => p !== platform) }
        : { ...prev, platforms: [...cur, platform] };
    });
  };

  const handleNext = () => { if (step < totalSteps - 1) setStep(step + 1); };
  const handleBack = () => { if (step > 0) setStep(step - 1); };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      await axios.post(`${API}/collaboration`, {
        brand_name: formData.brandName,
        brand_link: formData.brandLink,
        contact_name: formData.contactName,
        contact_email: formData.contactEmail,
        contact_phone: formData.contactPhone || '',
        platform: formData.platforms.join(', '),
        video_count: formData.videoCount,
        video_length: formData.videoLength,
        extra_ads: formData.extraAds,
        posting_date: formData.postingDate,
        has_brief: formData.hasBrief,
      });
      setIsSuccess(true);
    } catch (error) {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitError(false);
    setStep(0);
    setIsSuccess(false);
    setFormData({
      brandName: '', brandLink: '', contactName: '', contactEmail: '', contactPhone: '',
      platforms: [], videoCount: '', videoLength: '', extraAds: '', postingDate: '', hasBrief: '',
    });
    onClose();
  };

  const isCurrentStepValid = () => {
    switch (step) {
      case 0: return formData.brandName.trim() !== '' && formData.brandLink.trim() !== '';
      case 1: return formData.contactName.trim() !== '' && formData.contactEmail.trim() !== '' && formData.contactEmail.includes('@');
      case 2: return formData.platforms.length > 0;
      case 3: return formData.videoCount !== '';
      case 4: return formData.videoLength !== '';
      case 5: return formData.extraAds !== '';
      case 6: return formData.postingDate.trim() !== '';
      case 7: return formData.hasBrief !== '';
      default: return false;
    }
  };

  const stepNumberLabel = (n) => String(n).padStart(2, '0');

  const slideVariants = {
    enter: { opacity: 0, y: 18 },
    center: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -18 },
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <motion.div key="step0" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.6, ease: EASE }} className="space-y-8">
            <h3 className="font-heading italic text-3xl md:text-4xl text-text-primary leading-snug">
              {t.workWithMe.formSteps.brand}
            </h3>
            <div className="space-y-7">
              <input
                className="editorial-input w-full"
                placeholder={t.workWithMe.brandName}
                value={formData.brandName}
                onChange={(e) => handleInputChange('brandName', e.target.value)}
                data-testid="form-brand-name"
              />
              <input
                className="editorial-input w-full"
                placeholder={t.workWithMe.brandLink}
                value={formData.brandLink}
                onChange={(e) => handleInputChange('brandLink', e.target.value)}
                data-testid="form-brand-link"
              />
            </div>
          </motion.div>
        );
      case 1:
        return (
          <motion.div key="step1" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.6, ease: EASE }} className="space-y-8">
            <h3 className="font-heading italic text-3xl md:text-4xl text-text-primary leading-snug">
              {t.workWithMe.formSteps.contact}
            </h3>
            <div className="space-y-7">
              <input className="editorial-input w-full" placeholder={t.workWithMe.contactName} value={formData.contactName} onChange={(e) => handleInputChange('contactName', e.target.value)} data-testid="form-contact-name" />
              <input type="email" className="editorial-input w-full" placeholder={t.workWithMe.contactEmail} value={formData.contactEmail} onChange={(e) => handleInputChange('contactEmail', e.target.value)} data-testid="form-contact-email" />
              <div className="relative">
                <input type="tel" className="editorial-input w-full pr-24" placeholder={t.workWithMe.contactPhone} value={formData.contactPhone} onChange={(e) => handleInputChange('contactPhone', e.target.value)} data-testid="form-contact-phone" />
                <span className="absolute right-0 top-1/2 -translate-y-1/2 font-body text-[0.7rem] uppercase tracking-[0.25em] text-text-secondary">
                  {language === 'hu' ? 'opcionális' : 'optional'}
                </span>
              </div>
            </div>
          </motion.div>
        );
      case 2:
        return (
          <motion.div key="step2" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.6, ease: EASE }} className="space-y-8">
            <h3 className="font-heading italic text-3xl md:text-4xl text-text-primary leading-snug">
              {t.workWithMe.formSteps.platform}
            </h3>
            <p className="font-body text-[0.78rem] uppercase tracking-[0.25em] text-text-secondary">
              {language === 'hu' ? 'Válassz egyet vagy többet' : 'Select one or more'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {t.options.platforms.map((option) => (
                <OptionCard
                  key={option}
                  active={formData.platforms.includes(option)}
                  onClick={() => handlePlatformToggle(option)}
                  testId={`form-platform-${option.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {option}
                </OptionCard>
              ))}
            </div>
          </motion.div>
        );
      case 3:
        return (
          <motion.div key="step3" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.6, ease: EASE }} className="space-y-8">
            <h3 className="font-heading italic text-3xl md:text-4xl text-text-primary leading-snug">
              {t.workWithMe.formSteps.videoCount}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {t.options.videoCounts.map((option) => (
                <OptionCard
                  key={option}
                  active={formData.videoCount === option}
                  onClick={() => handleInputChange('videoCount', option)}
                  testId={`form-video-count-${option.replace(/\s/g, '-')}`}
                >
                  {option}
                </OptionCard>
              ))}
            </div>
          </motion.div>
        );
      case 4:
        return (
          <motion.div key="step4" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.6, ease: EASE }} className="space-y-8">
            <h3 className="font-heading italic text-3xl md:text-4xl text-text-primary leading-snug">
              {t.workWithMe.formSteps.videoLength}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {t.options.videoLengths.map((option) => (
                <OptionCard
                  key={option}
                  active={formData.videoLength === option}
                  onClick={() => handleInputChange('videoLength', option)}
                  testId={`form-video-length-${option.replace(/\s/g, '-')}`}
                >
                  {option}
                </OptionCard>
              ))}
            </div>
          </motion.div>
        );
      case 5:
        return (
          <motion.div key="step5" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.6, ease: EASE }} className="space-y-8">
            <h3 className="font-heading italic text-3xl md:text-4xl text-text-primary leading-snug">
              {t.workWithMe.formSteps.extraAds}
            </h3>
            <div className="space-y-3">
              {t.options.extraAds.map((option) => (
                <OptionCard
                  key={option}
                  active={formData.extraAds === option}
                  onClick={() => handleInputChange('extraAds', option)}
                  testId={`form-extra-ads-${option.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {option}
                </OptionCard>
              ))}
            </div>
          </motion.div>
        );
      case 6:
        return (
          <motion.div key="step6" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.6, ease: EASE }} className="space-y-8">
            <h3 className="font-heading italic text-3xl md:text-4xl text-text-primary leading-snug">
              {t.workWithMe.formSteps.postingDate}
            </h3>
            <input
              type="text"
              className="editorial-input w-full"
              placeholder={language === 'hu' ? 'pl. 2025 február vége' : 'e.g. end of February 2025'}
              value={formData.postingDate}
              onChange={(e) => handleInputChange('postingDate', e.target.value)}
              data-testid="form-posting-date"
            />
          </motion.div>
        );
      case 7:
        return (
          <motion.div key="step7" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.6, ease: EASE }} className="space-y-8">
            <h3 className="font-heading italic text-3xl md:text-4xl text-text-primary leading-snug">
              {t.workWithMe.formSteps.brief}
            </h3>
            <div className="space-y-3">
              {t.options.brief.map((option) => (
                <OptionCard
                  key={option}
                  active={formData.hasBrief === option}
                  onClick={() => handleInputChange('hasBrief', option)}
                  testId={`form-brief-${option.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {option}
                </OptionCard>
              ))}
            </div>
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-[560px] bg-background border border-border/70 p-0 overflow-hidden rounded-3xl shadow-editorial"
        data-testid="work-with-me-modal"
      >
        {/* Header */}
        <div className="relative px-8 md:px-10 pt-9 pb-5">
          <div className="absolute -top-24 -right-24 w-[280px] h-[280px] rounded-full bg-gradient-to-br from-accent/30 to-transparent blur-3xl pointer-events-none" />
          <p className="font-body text-[0.65rem] uppercase tracking-[0.32em] text-accent">
            {language === 'hu' ? 'Együttműködés' : 'Collaboration'}
          </p>
          <h2 className="mt-2 font-heading text-3xl md:text-4xl text-text-primary leading-tight">
            {language === 'hu' ? 'Beszéljünk a' : 'Let\'s talk'}
            <span className="italic font-extralight text-text-secondary"> {language === 'hu' ? 'márkádról.' : 'about your brand.'}</span>
          </h2>
        </div>

        {/* Progress */}
        <div className="px-8 md:px-10">
          <div className="flex items-center justify-between mb-3">
            <span className="font-body text-[0.65rem] uppercase tracking-[0.3em] text-text-secondary">
              {language === 'hu' ? 'Lépés' : 'Step'} {stepNumberLabel(step + 1)} / {stepNumberLabel(totalSteps)}
            </span>
            <span className="font-body text-[0.65rem] uppercase tracking-[0.3em] text-text-secondary">
              {Math.round(((step + 1) / totalSteps) * 100)}%
            </span>
          </div>
          <div className="w-full h-px bg-border relative overflow-hidden">
            <motion.div
              className="absolute inset-y-0 left-0 bg-accent"
              initial={{ width: 0 }}
              animate={{ width: `${((step + 1) / totalSteps) * 100}%` }}
              transition={{ duration: 0.6, ease: EASE }}
            />
          </div>
        </div>

        {/* Body */}
        <div className="px-8 md:px-10 pt-8 pb-10 min-h-[300px]">
          {isSuccess ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="py-10 text-center"
            >
              <div className="mx-auto w-16 h-16 rounded-full border border-accent flex items-center justify-center mb-6">
                <CheckCircle2 size={28} strokeWidth={1.25} className="text-accent" />
              </div>
              <p className="font-heading italic text-2xl md:text-3xl text-text-primary leading-snug">
                {t.workWithMe.success}
              </p>
            </motion.div>
          ) : (
            <>
              <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>

              {submitError && (
                <p className="mt-6 text-center font-body text-sm text-red-700" role="alert" data-testid="form-error">
                  {language === 'hu' ? 'Valami hiba történt. Kérjük, próbáld újra.' : 'Something went wrong. Please try again.'}
                </p>
              )}

              {/* Navigation */}
              <div className="flex justify-between items-center mt-10 pt-6 border-t border-border/70">
                <Button
                  variant="ghost"
                  onClick={handleBack}
                  disabled={step === 0}
                  className="font-body text-[0.78rem] uppercase tracking-[0.25em] text-text-secondary hover:text-text-primary hover:bg-transparent disabled:opacity-30 px-0"
                  data-testid="form-back-btn"
                >
                  <ArrowLeft size={16} className="mr-2" strokeWidth={1.5} />
                  {t.workWithMe.back}
                </Button>

                {step === totalSteps - 1 ? (
                  <button
                    onClick={handleSubmit}
                    disabled={!isCurrentStepValid() || isSubmitting}
                    className="btn-magnetic group inline-flex items-center gap-3 bg-text-primary text-background pl-6 pr-2.5 py-2.5 rounded-full hover:bg-accent transition-colors duration-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="form-submit-btn"
                  >
                    <span className="font-body text-[0.75rem] uppercase tracking-[0.25em]">{t.workWithMe.submit}</span>
                    <span className="flex items-center justify-center w-9 h-9 rounded-full bg-background text-text-primary">
                      {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} strokeWidth={1.5} />}
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    disabled={!isCurrentStepValid()}
                    className="btn-magnetic group inline-flex items-center gap-3 bg-text-primary text-background pl-6 pr-2.5 py-2.5 rounded-full hover:bg-accent transition-colors duration-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="form-next-btn"
                  >
                    <span className="font-body text-[0.75rem] uppercase tracking-[0.25em]">{t.workWithMe.next}</span>
                    <span className="flex items-center justify-center w-9 h-9 rounded-full bg-background text-text-primary group-hover:rotate-45 transition-transform duration-700">
                      <ArrowRight size={14} strokeWidth={1.5} />
                    </span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkWithMeModal;
